import "./App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import axios from "axios";

// Only eagerly load login + launcher (first screens users see)
import LoginPage from "./pages/LoginPage";
import LauncherPage from "./pages/LauncherPage";
import PaymentWarningBanner from "./components/PaymentWarningBanner";

// Lazy load ALL other pages for faster initial load
const ManagerDashboard = lazy(() => import("./pages/ManagerDashboard"));
const TeamManagerDashboard = lazy(() => import("./pages/TeamManagerDashboard"));
const DashboardPage = lazy(() => import("./pages/Dashboard"));
const AccountingPage = lazy(() => import("./pages/AccountingPage"));
const CombinedSalesDashboard = lazy(() => import("./pages/CombinedSalesDashboard"));
const SettingsPage = lazy(() => import("./pages/CommercialSettingsPage"));
const AdvisorDashboard = lazy(() => import("./pages/AdvisorDashboard"));
const ReconPage = lazy(() => import("./pages/ReconPage"));
const MarketingPage = lazy(() => import("./pages/MarketingPage"));
const InventoryPage = lazy(() => import("./pages/InventoryPage"));
const VehicleDetailPage = lazy(() => import("./pages/VehicleDetailPage"));
const WholesaleContractsPage = lazy(() => import("./pages/WholesaleContractsPage"));
const CashflowPage = lazy(() => import("./pages/CashflowPage"));
const CommissionPage = lazy(() => import("./pages/CommissionPage"));
const DigitalSalesBoard = lazy(() => import("./pages/DigitalSalesBoard"));
const UserManagement = lazy(() => import("./pages/UserManagement"));
const DebugDashboard = lazy(() => import("./pages/DebugDashboard"));
const BudgetPage = lazy(() => import("./pages/BudgetPage"));
const DepositsPage = lazy(() => import("./pages/DepositsPage"));
const AppraisalPage = lazy(() => import("./pages/AppraisalPage"));
const WholesalerBidPage = lazy(() => import("./pages/WholesalerBidPage"));
const DealerManagementPage = lazy(() => import("./pages/DealerManagementPage"));
const DealerSettingsPage = lazy(() => import("./pages/DealerSettingsPage"));
const DealerPortalPage = lazy(() => import("./pages/DealerPortalPage"));
const DealerOnboardingPage = lazy(() => import("./pages/DealerOnboardingPage"));
const FBMarketplacePage = lazy(() => import("./pages/FBMarketplacePage"));
const FBPosterAdminPage = lazy(() => import("./pages/FBPosterAdminPage"));
const PresidentDashboard = lazy(() => import("./pages/PresidentDashboard"));
const AutomationsPage = lazy(() => import("./pages/AutomationsPage"));
const GmailSyncPage = lazy(() => import("./pages/GmailSyncPage"));
const GmailSyncCallback = lazy(() => import("./pages/GmailSyncCallback"));
const UnmatchedSMSPage = lazy(() => import("./pages/UnmatchedSMSPage"));
const TodayPage = lazy(() => import("./pages/TodayPage"));
const LeadProtectionPage = lazy(() => import("./pages/LeadProtectionPage"));
const MessagesPage = lazy(() => import("./pages/MessagesPage"));
const KPIDashboard = lazy(() => import("./pages/KPIDashboard"));
const LeadFlowBoardPage = lazy(() => import("./pages/LeadFlowBoardPage"));
const FloorplanPage = lazy(() => import("./pages/FloorplanPage"));
const SupplierPaymentsPage = lazy(() => import("./pages/SupplierPaymentsPage"));
const SMSMobileApp = lazy(() => import("./pages/SMSMobileApp"));
const PageCustomizationPage = lazy(() => import("./pages/PageCustomizationPage"));
const PlatformAdminDashboard = lazy(() => import("./pages/PlatformAdminDashboard"));
const PublicSignupPage = lazy(() => import("./pages/PublicSignupPage"));
const DealerProfileSettingsPage = lazy(() => import("./pages/DealerProfileSettingsPage"));
const SetupPage = lazy(() => import("./pages/SetupPage"));
const SuperAdminPage = lazy(() => import("./pages/SuperAdminPage"));
const SubscriptionPage = lazy(() => import("./pages/SubscriptionPage"));
const BillingPage = lazy(() => import("./pages/BillingPage"));
const ForgotPasswordPage = lazy(() => import("./pages/ForgotPasswordPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const LandingPage = lazy(() => import("./pages/LandingPage"));
const AboutPage = lazy(() => import("./pages/AboutPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const WebsiteManagementPage = lazy(() => import("./pages/WebsiteManagementPage"));
const DealerPublicSite = lazy(() => import("./pages/DealerPublicSite"));
const StaffTrackerPage = lazy(() => import("./pages/StaffTrackerPage"));
const TimesheetPage = lazy(() => import("./pages/TimesheetPage"));
const WorkOrdersPage = lazy(() => import("./pages/WorkOrdersPage"));
const SupplierManagementPage = lazy(() => import("./pages/SupplierManagementPage"));
const VehiclePnLPage = lazy(() => import("./pages/VehiclePnLPage"));
const SystemAdminPage = lazy(() => import("./pages/SystemAdminPage"));
const ActivityMonitorPage = lazy(() => import("./pages/ActivityMonitorPage"));
import ConnectionStatus from "./components/ConnectionStatus";  // P0 BULLETPROOF: Server status indicator
import PWAInstallPrompt from "./components/PWAInstallPrompt";
import UpdateNotification from "./components/UpdateNotification";
import MobileQuickAccess from "./components/MobileQuickAccess";
import MobileBottomNav from "./components/MobileBottomNav";
import CircuitBreakerBanner from "./components/CircuitBreakerBanner";
import ErrorBoundary from "./components/ErrorBoundary";
import GlobalSMSNotifications from "./components/GlobalSMSNotifications";
import { TeamsProvider } from "./contexts/TeamsContext";
import { KPISettingsProvider } from "./components/KPISettings";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import { ModuleAccessProvider, useModuleAccess } from "./contexts/ModuleAccessContext";
import { RolePermissionsProvider } from "./contexts/RolePermissionsContext";
import { DemoTourProvider } from "./contexts/DemoTourContext";
import { useActivityTracker } from "./hooks/useActivityTracker";
import errorLogger from "./utils/errorLogger";
import { getBackendUrl } from "./utils/apiConfig";
import AppHeader from "./components/AppHeader";

// Lazy load ReportsPage to avoid initialization issues
const ReportsPage = lazy(() => import("./pages/ReportsPage"));

const BACKEND_URL = getBackendUrl();

// BUILD VERSION STAMP - Used to verify deployed code is up-to-date
const APP_BUILD_VERSION = 'BUILD-2025-01-17-STORAGE-FIX';
console.log('🏗️ APP BUILD VERSION:', APP_BUILD_VERSION);
console.log('🌐 BACKEND URL:', BACKEND_URL);

// STORAGE CLEANUP - Clear excessive cache data to prevent QuotaExceededError
const cleanupLocalStorage = () => {
  try {
    // Check total localStorage usage
    let totalSize = 0;
    for (let key in localStorage) {
      if (localStorage.hasOwnProperty(key)) {
        totalSize += (localStorage[key]?.length || 0) * 2; // UTF-16 = 2 bytes per char
      }
    }
    const usageMB = (totalSize / (1024 * 1024)).toFixed(2);
    console.log(`📊 localStorage usage: ${usageMB} MB`);
    
    // If usage exceeds 4MB, clear non-essential caches
    if (totalSize > 4 * 1024 * 1024) {
      console.log('⚠️ localStorage near quota, clearing old caches...');
      const cachesToClear = [
        'crm_cache_leads',
        'manager_cache_eamData',
        'manager_cache_nitinData',
        'sales_cache_eamSales',
        'sales_cache_nitinSales'
      ];
      cachesToClear.forEach(key => localStorage.removeItem(key));
      localStorage.removeItem('lastPrefetch');
      console.log('✅ Old caches cleared');
    }
  } catch (e) {
    console.warn('Storage cleanup error:', e);
  }
};

// Run cleanup on app load
cleanupLocalStorage();

// GLOBAL DATA PREFETCH - Runs once on app load to pre-populate all page caches
const prefetchAllData = async () => {
  const token = localStorage.getItem('token');
  if (!token) {
    console.log('⏭️ Prefetch skipped - no token');
    return;
  }
  
  const now = Date.now();
  const lastPrefetch = localStorage.getItem('lastPrefetch');
  // Only prefetch if not done in last 30 seconds
  if (lastPrefetch && (now - parseInt(lastPrefetch)) < 30000) {
    console.log('⏭️ Prefetch skipped - done recently');
    return;
  }
  
  localStorage.setItem('lastPrefetch', now.toString());
  console.log('🚀 Starting prefetch of all page data...');
  
  try {
    const headers = { 
      'Authorization': `Bearer ${token}`,
      'Cache-Control': 'no-cache' 
    };
    const month = new Date().getMonth() + 1;
    const year = new Date().getFullYear();
    
    // Fetch all data in parallel
    const results = await Promise.allSettled([
      axios.get(`${BACKEND_URL}/api/manager-dashboard/combined?month=${month}&year=${year}`, { headers, timeout: 10000 }),
      axios.get(`${BACKEND_URL}/api/sales-dashboard/combined?month=${month}&year=${year}&view_mode=monthly`, { headers, timeout: 10000 }),
      axios.get(`${BACKEND_URL}/api/leads`, { headers, timeout: 10000 })
    ]);
    
    const [dashboardRes, salesRes, leadsRes] = results;
    
    // Cache Dashboard data
    if (dashboardRes.status === 'fulfilled' && dashboardRes.value?.data) {
      const d = dashboardRes.value.data;
      // Dynamic teams cache
      const teamNames = d.team_names || Object.keys(d.teams || {});
      teamNames.forEach(tn => {
        const td = d.teams?.[tn] || d[tn] || {};
        localStorage.setItem(`manager_cache_team_${tn}_data`, JSON.stringify({ sales: td.sales || [], objectives: td.objectives || {} }));
        localStorage.setItem(`manager_cache_team_${tn}_advisors`, JSON.stringify(td.advisors || []));
      });
      if (d.eam) localStorage.setItem('manager_cache_eamData', JSON.stringify({ sales: d.eam?.sales || [], objectives: d.eam?.objectives || {} }));
      if (d.nitin) localStorage.setItem('manager_cache_nitinData', JSON.stringify({ sales: d.nitin?.sales || [], objectives: d.nitin?.objectives || {} }));
      if (d.eam) localStorage.setItem('manager_cache_eamAdvisors', JSON.stringify(d.eam?.advisors || []));
      if (d.nitin) localStorage.setItem('manager_cache_nitinAdvisors', JSON.stringify(d.nitin?.advisors || []));
      localStorage.setItem('manager_cache_alerts', JSON.stringify(d.alerts || {}));
      localStorage.setItem('manager_cache_inventoryStats', JSON.stringify(d.inventory || {}));
      localStorage.setItem('manager_cache_marketingStats', JSON.stringify(d.marketing || {}));
      console.log('✅ Dashboard cache populated');
    }
    
    // Cache Sales page data
    if (salesRes.status === 'fulfilled' && salesRes.value?.data) {
      const s = salesRes.value.data;
      const teamNames = s.team_names || Object.keys(s.teams || {});
      teamNames.forEach(tn => {
        const td = s.teams?.[tn] || s[tn] || {};
        localStorage.setItem(`sales_cache_team_${tn}_sales`, JSON.stringify(td.sales || []));
        localStorage.setItem(`sales_cache_team_${tn}_advisors`, JSON.stringify(td.advisors || []));
        localStorage.setItem(`sales_cache_team_${tn}_objectives`, JSON.stringify(td.objectives || {}));
      });
      if (s.eam) localStorage.setItem('sales_cache_eamSales', JSON.stringify(s.eam?.sales || []));
      if (s.nitin) localStorage.setItem('sales_cache_nitinSales', JSON.stringify(s.nitin?.sales || []));
      if (s.eam) localStorage.setItem('sales_cache_eamAdvisors', JSON.stringify(s.eam?.advisors || []));
      if (s.nitin) localStorage.setItem('sales_cache_nitinAdvisors', JSON.stringify(s.nitin?.advisors || []));
      if (s.eam) localStorage.setItem('sales_cache_eamObjectives', JSON.stringify(s.eam?.objectives || {}));
      if (s.nitin) localStorage.setItem('sales_cache_nitinObjectives', JSON.stringify(s.nitin?.objectives || {}));
      localStorage.setItem('sales_cache_banks', JSON.stringify(s.banks || []));
      localStorage.setItem('sales_cache_ytdStats', JSON.stringify(s.ytd || null));
      console.log('✅ Sales cache populated');
    }
    
    // Cache CRM/Marketing page data (limited to prevent QuotaExceededError)
    if (leadsRes.status === 'fulfilled' && leadsRes.value?.data) {
      try {
        // Only cache the most recent 50 leads to avoid localStorage quota issues
        const allLeads = leadsRes.value.data;
        const recentLeads = Array.isArray(allLeads) 
          ? allLeads.slice(0, 50) 
          : allLeads;
        localStorage.setItem('crm_cache_leads', JSON.stringify(recentLeads));
        console.log('✅ CRM cache populated (limited to 50 leads)');
      } catch (storageError) {
        // If storage is full, clear old cache and skip caching leads
        console.warn('localStorage quota exceeded, clearing old cache:', storageError.message);
        localStorage.removeItem('crm_cache_leads');
      }
    }
    
    console.log('✅ All page data prefetch complete');
  } catch (e) {
    console.warn('Prefetch error:', e);
  }
};

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  // OPTIMIZATION: Check localStorage directly for instant render
  // This prevents the "Chargement..." screen when we already have auth data
  const hasStoredAuth = localStorage.getItem('token') && localStorage.getItem('user');

  // Only show loading if we're actually checking auth AND don't have stored credentials
  if (loading && !hasStoredAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Chargement...</p>
        </div>
      </div>
    );
  }

  // If we have stored auth data OR isAuthenticated is true, render children
  // The auth context will validate in background and redirect if invalid
  if (hasStoredAuth || isAuthenticated) {
    return children;
  }

  // Preserve the intended destination in the redirect
  return <Navigate to="/login" state={{ from: location }} replace />;
};

// Global header that renders on all authenticated pages (except exclusions)
// Excluded: pages with their own left sidebar navigation, pages with their own AppHeader, and non-app pages
const HEADER_EXCLUDED_PATHS = [
  // Non-app pages
  '/', '/login', '/signup', '/forgot-password', '/reset-password', '/home', '/sms', '/sms-app',
  '/dealer-portal', '/fb-poster', '/dealer-onboarding', '/gmail-sync/callback',
  // Pages with LEFT SIDEBAR navigation (don't need top header):
  '/appraisals', '/sales-dashboard', '/inventory',
  '/lead-protection', '/manager-dashboard', '/marketing', '/today',
  '/wholesale-contracts', '/sales-board', '/messages',
  '/accounting', '/advisor-dashboard', '/settings',
  '/fb-marketplace', '/fb-poster-admin',
  '/kpi-dashboard', '/lead-flow-board',
  '/recon', '/reports',
  '/sales-rep-dashboard', '/team-dashboard', '/users',
  '/setup',
  // Additional sidebar pages:
  '/cashflow', '/work-orders', '/president-dashboard',
  '/vehicle-pnl', '/budget', '/staff-tracker',
  '/suppliers', '/timesheet', '/dashboard',
  '/deposits', '/supplier-payments', '/system-admin',
  '/floorplan', '/dealer-website', '/activity-monitor',
];
const GlobalHeader = () => {
  const { isAuthenticated } = useAuth();
  const location = useLocation();
  const hasStoredAuth = localStorage.getItem('token');
  
  if (!hasStoredAuth && !isAuthenticated) return null;
  if (HEADER_EXCLUDED_PATHS.some(p => location.pathname === p || location.pathname.startsWith(p + '/'))) return null;
  
  return <AppHeader />;
};

// Marketing Only Route - redirects non-marketing pages for marketing-only users
const MarketingOnlyGuard = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  
  const MARKETING_ONLY_USERS = ['maxime@glive.ca'];
  const isMarketingOnly = MARKETING_ONLY_USERS.includes(user?.email?.toLowerCase());
  
  // Pages allowed for marketing-only users
  const ALLOWED_PATHS = ['/marketing', '/lead-flow-board', '/kpi-dashboard', '/home'];
  
  // If marketing-only user tries to access non-allowed page, redirect to home
  if (isMarketingOnly && !ALLOWED_PATHS.includes(location.pathname)) {
    return <Navigate to="/home" replace />;
  }
  
  return children;
};

// Role-based dashboard redirect
const DashboardRedirect = () => {
  const { user } = useAuth();
  
  // Block sales reps - no access
  if (user?.role === 'sales_rep') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center p-8 bg-white rounded-lg shadow-lg max-w-md">
          <h1 className="text-2xl font-bold text-red-600 mb-4">Accès Restreint</h1>
          <p className="text-slate-600 mb-4">
            L'accès des représentants a été désactivé.
          </p>
          <p className="text-slate-500 text-sm">
            Veuillez contacter votre administrateur pour plus d'informations.
          </p>
        </div>
      </div>
    );
  }
  
  // Everyone else goes to the launcher page
  return <Navigate to="/home" />;
};

function AppRoutes() {
  const location = window.location.pathname;
  const isPublicSite = location.startsWith('/site/');

  // Track user activity
  useActivityTracker();

  if (isPublicSite) {
    return (
      <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div></div>}>
        <Routes>
          <Route path="/site/:slug/*" element={<DealerPublicSite />} />
        </Routes>
      </Suspense>
    );
  }

  return (
    <>
    <PaymentWarningBanner />
    <GlobalHeader />
    <Suspense fallback={<div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div></div>}>
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/signup" element={<PublicSignupPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      {/* Public route for wholesaler bidding - no auth required */}
      <Route path="/bid/:bidToken" element={<WholesalerBidPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/blog" element={<BlogPage />} />
      <Route path="/" element={<LandingPage />} />
      
      {/* Home Launcher - iPhone style app grid */}
      <Route path="/home" element={<ProtectedRoute><LauncherPage /></ProtectedRoute>} />
      <Route path="/manager-dashboard" element={<ProtectedRoute><MarketingOnlyGuard><ManagerDashboard /></MarketingOnlyGuard></ProtectedRoute>} />
      {/* REMOVED: /president-dashboard - not needed for corner dealers */}
      <Route path="/team-dashboard" element={<ProtectedRoute><MarketingOnlyGuard><TeamManagerDashboard /></MarketingOnlyGuard></ProtectedRoute>} />
      <Route path="/advisor-dashboard" element={<ProtectedRoute><MarketingOnlyGuard><AdvisorDashboard /></MarketingOnlyGuard></ProtectedRoute>} />
      {/* Sales rep route removed - access disabled */}
      {/* REMOVED: /dashboard - not needed for corner dealers */}
      <Route path="/sales-dashboard" element={<ProtectedRoute><MarketingOnlyGuard><CombinedSalesDashboard /></MarketingOnlyGuard></ProtectedRoute>} />
      <Route path="/sales" element={<Navigate to="/sales-dashboard" replace />} />
      <Route path="/deposits" element={<ProtectedRoute><MarketingOnlyGuard><DepositsPage /></MarketingOnlyGuard></ProtectedRoute>} />
      <Route path="/commissions" element={<ProtectedRoute><MarketingOnlyGuard><CommissionPage /></MarketingOnlyGuard></ProtectedRoute>} />
      {/* Accounting / Comptabilité - Admin + Accountant only */}
      <Route path="/accounting" element={<ProtectedRoute><MarketingOnlyGuard><AccountingPage /></MarketingOnlyGuard></ProtectedRoute>} />
      <Route path="/comptabilite" element={<Navigate to="/accounting" replace />} />
      <Route path="/inventory" element={<ProtectedRoute><MarketingOnlyGuard><InventoryPage /></MarketingOnlyGuard></ProtectedRoute>} />
      <Route path="/inventory/:stock" element={<ProtectedRoute><MarketingOnlyGuard><VehicleDetailPage /></MarketingOnlyGuard></ProtectedRoute>} />
      <Route path="/wholesale-contracts" element={<ProtectedRoute><MarketingOnlyGuard><WholesaleContractsPage /></MarketingOnlyGuard></ProtectedRoute>} />
      <Route path="/recon" element={<ProtectedRoute><MarketingOnlyGuard><ReconPage /></MarketingOnlyGuard></ProtectedRoute>} />
      {/* REMOVED: /cashflow (Trésorerie) - not needed for corner dealers */}
      {/* Removed: Nitin Commissions Page - now integrated in sales dashboard */}
      <Route path="/users" element={<ProtectedRoute><MarketingOnlyGuard><UserManagement /></MarketingOnlyGuard></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><MarketingOnlyGuard><SettingsPage /></MarketingOnlyGuard></ProtectedRoute>} />
      <Route path="/dealer-profile" element={<ProtectedRoute><MarketingOnlyGuard><DealerProfileSettingsPage /></MarketingOnlyGuard></ProtectedRoute>} />
      <Route path="/page-customization" element={<ProtectedRoute><MarketingOnlyGuard><PageCustomizationPage /></MarketingOnlyGuard></ProtectedRoute>} />
      <Route path="/platform-admin" element={<ProtectedRoute><MarketingOnlyGuard><PlatformAdminDashboard /></MarketingOnlyGuard></ProtectedRoute>} />
      <Route path="/setup" element={<ProtectedRoute><MarketingOnlyGuard><SetupPage /></MarketingOnlyGuard></ProtectedRoute>} />
      <Route path="/super-admin" element={<ProtectedRoute><SuperAdminPage /></ProtectedRoute>} />
      <Route path="/system-admin" element={<ProtectedRoute><MarketingOnlyGuard><SystemAdminPage /></MarketingOnlyGuard></ProtectedRoute>} />
      <Route path="/activity-monitor" element={<ProtectedRoute><ActivityMonitorPage /></ProtectedRoute>} />
      <Route path="/staff-tracker" element={<ProtectedRoute><MarketingOnlyGuard><StaffTrackerPage /></MarketingOnlyGuard></ProtectedRoute>} />
      <Route path="/timesheet" element={<ProtectedRoute><TimesheetPage /></ProtectedRoute>} />
      <Route path="/work-orders" element={<ProtectedRoute><MarketingOnlyGuard><WorkOrdersPage /></MarketingOnlyGuard></ProtectedRoute>} />
      <Route path="/suppliers" element={<ProtectedRoute><MarketingOnlyGuard><SupplierManagementPage /></MarketingOnlyGuard></ProtectedRoute>} />
      <Route path="/vehicle-pnl" element={<ProtectedRoute><MarketingOnlyGuard><VehiclePnLPage /></MarketingOnlyGuard></ProtectedRoute>} />
      <Route path="/subscription" element={<ProtectedRoute><SubscriptionPage /></ProtectedRoute>} />
      <Route path="/billing" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
      <Route path="/billing/success" element={<ProtectedRoute><BillingPage /></ProtectedRoute>} />
      <Route path="/dealer-website" element={<ProtectedRoute><WebsiteManagementPage /></ProtectedRoute>} />
      <Route path="/automations" element={<ProtectedRoute><MarketingOnlyGuard><AutomationsPage /></MarketingOnlyGuard></ProtectedRoute>} />
      <Route path="/reports" element={<ProtectedRoute><MarketingOnlyGuard><ReportsPage /></MarketingOnlyGuard></ProtectedRoute>} />
      {/* Removed: Recon/Comptabilite Page */}
      {/* Marketing page - accessible to marketing-only users */}
      <Route path="/marketing" element={<ProtectedRoute><MarketingPage /></ProtectedRoute>} />
      <Route path="/crm" element={<Navigate to="/marketing" replace />} />
      {/* Messages - Dedicated SMS Messages Page */}
      <Route path="/messages" element={<ProtectedRoute><MarketingOnlyGuard><MessagesPage /></MarketingOnlyGuard></ProtectedRoute>} />
      {/* KPI Dashboard - Accessible to marketing-only users */}
      <Route path="/kpi-dashboard" element={<ProtectedRoute><KPIDashboard /></ProtectedRoute>} />
      {/* Lead Flow Board - Accessible to marketing-only users */}
      <Route path="/lead-flow-board" element={<ProtectedRoute><LeadFlowBoardPage /></ProtectedRoute>} />
      {/* Today - My Actions (Cadence) */}
      <Route path="/today" element={<ProtectedRoute><MarketingOnlyGuard><TodayPage /></MarketingOnlyGuard></ProtectedRoute>} />
      {/* Lead Protection Dashboard - Bulletproof Lead Tracking */}
      <Route path="/lead-protection" element={<ProtectedRoute><MarketingOnlyGuard><LeadProtectionPage /></MarketingOnlyGuard></ProtectedRoute>} />
      <Route path="/sales-board" element={<ProtectedRoute><MarketingOnlyGuard><DigitalSalesBoard /></MarketingOnlyGuard></ProtectedRoute>} />
      <Route path="/dashboard" element={<ProtectedRoute><MarketingOnlyGuard><DashboardPage /></MarketingOnlyGuard></ProtectedRoute>} />
      <Route path="/president-dashboard" element={<ProtectedRoute><MarketingOnlyGuard><PresidentDashboard /></MarketingOnlyGuard></ProtectedRoute>} />
      <Route path="/cashflow" element={<ProtectedRoute><MarketingOnlyGuard><CashflowPage /></MarketingOnlyGuard></ProtectedRoute>} />
      <Route path="/budget" element={<ProtectedRoute><MarketingOnlyGuard><BudgetPage /></MarketingOnlyGuard></ProtectedRoute>} />
      <Route path="/floorplan" element={<ProtectedRoute><MarketingOnlyGuard><FloorplanPage /></MarketingOnlyGuard></ProtectedRoute>} />
      <Route path="/dealers" element={<ProtectedRoute><MarketingOnlyGuard><DealerManagementPage /></MarketingOnlyGuard></ProtectedRoute>} />
      <Route path="/dealers/:dealerId/settings" element={<ProtectedRoute><DealerSettingsPage /></ProtectedRoute>} />
      <Route path="/dealer-portal" element={<DealerPortalPage />} />
      <Route path="/dealer-portal/:dealerSlug" element={<DealerPortalPage />} />
      <Route path="/dealer-onboarding" element={<DealerOnboardingPage />} />
      <Route path="/debug" element={<ProtectedRoute><MarketingOnlyGuard><DebugDashboard /></MarketingOnlyGuard></ProtectedRoute>} />
      {/* REMOVED: /budget - not needed for corner dealers */}
      {/* Removed: Delivery calendar route - delivery module disabled */}
      <Route path="/appraisals" element={<ProtectedRoute><MarketingOnlyGuard><AppraisalPage /></MarketingOnlyGuard></ProtectedRoute>} />
      {/* REMOVED: Dealer Management routes - not needed for corner dealers */}
      {/* FB Marketplace Bot */}
      <Route path="/fb-marketplace" element={<ProtectedRoute><MarketingOnlyGuard><FBMarketplacePage /></MarketingOnlyGuard></ProtectedRoute>} />
      {/* Marketing AI Agent */}
      {/* Removed: Marketing AI route - marketing AI module disabled */}
      {/* Gmail Sync - OAuth-based email lead import */}
      <Route path="/gmail-sync" element={<ProtectedRoute><MarketingOnlyGuard><GmailSyncPage /></MarketingOnlyGuard></ProtectedRoute>} />
      <Route path="/gmail-sync-callback" element={<GmailSyncCallback />} />
      {/* Unmatched SMS - Admin only */}
      <Route path="/admin/sms-unmatched" element={<ProtectedRoute><MarketingOnlyGuard><UnmatchedSMSPage /></MarketingOnlyGuard></ProtectedRoute>} />
      {/* Redirect old scheduler URL to main FB page */}
      <Route path="/fb-scheduler" element={<Navigate to="/fb-marketplace" replace />} />
      {/* REMOVED: Dealer Portal routes - not needed for corner dealers */}
      {/* FB Poster Admin - Manage SaaS Dealers */}
      <Route path="/fb-poster-admin" element={<FBPosterAdminPage />} />
      
      {/* REMOVED: /floorplan - not needed for corner dealers */}
      
      {/* Supplier Payments - Monthly supplier payment tracking */}
      <Route path="/supplier-payments" element={<ProtectedRoute><MarketingOnlyGuard><SupplierPaymentsPage /></MarketingOnlyGuard></ProtectedRoute>} />
      
      {/* SMS Mobile App - Standalone PWA for analysts (WhatsApp-style) */}
      <Route path="/sms" element={<ProtectedRoute><SMSMobileApp /></ProtectedRoute>} />
      <Route path="/sms-app" element={<ProtectedRoute><SMSMobileApp /></ProtectedRoute>} />
    </Routes>
    </Suspense>
    </>
  );
}

function App() {
  // PREFETCH all page data on app load for instant navigation
  useEffect(() => {
    prefetchAllData();
  }, []);
  
  // PRODUCTION SAFETY: Handle version conflict events (optimistic locking)
  useEffect(() => {
    const handleVersionConflict = (event) => {
      const { message } = event.detail;
      // Show alert to user
      alert(`⚠️ Conflit de version\n\n${message}`);
      // Optionally refresh the page to get latest data
      // window.location.reload();
    };
    
    window.addEventListener('versionConflict', handleVersionConflict);
    return () => window.removeEventListener('versionConflict', handleVersionConflict);
  }, []);
  
  // PRODUCTION RESILIENCE: Silent retries - only log to console, no user notification
  // The retry logic in safeFetch.js handles 502/503 errors automatically
  useEffect(() => {
    const handleApiRetry = (event) => {
      const { attempt, maxRetries, status, url } = event.detail;
      // Silent retry - just log to console for debugging
      console.log(`🔄 API retry ${attempt}/${maxRetries} for ${url} (${status})`);
    };
    
    const handleApiSuccess = (event) => {
      const { url, attempts } = event.detail || {};
      if (attempts > 1) {
        console.log(`✅ API recovered after ${attempts} attempts: ${url}`);
      }
    };
    
    window.addEventListener('apiRetry', handleApiRetry);
    window.addEventListener('apiSuccess', handleApiSuccess);
    
    return () => {
      window.removeEventListener('apiRetry', handleApiRetry);
      window.removeEventListener('apiSuccess', handleApiSuccess);
    };
  }, []);
  
  // Force cleanup of ALL service workers and caches
  useEffect(() => {
    if ('serviceWorker' in navigator) {
      // Listen for reload message from cleanup service worker
      navigator.serviceWorker.addEventListener('message', (event) => {
        if (event.data && event.data.type === 'RELOAD') {
          console.log('🔄 Service Worker cleanup complete - reloading page...');
          window.location.reload();
        }
      });
      
      // Force unregister all service workers
      navigator.serviceWorker.getRegistrations().then((registrations) => {
        if (registrations.length > 0) {
          console.log(`⚠️ Found ${registrations.length} Service Worker(s) - forcing cleanup...`);
          registrations.forEach((registration) => {
            registration.unregister().then(() => {
              console.log('✅ Service Worker unregistered');
            });
          });
        } else {
          console.log('✅ No Service Workers found - clean state');
        }
      });
      
      // Clear all caches
      if ('caches' in window) {
        caches.keys().then((cacheNames) => {
          if (cacheNames.length > 0) {
            console.log(`🗑️ Deleting ${cacheNames.length} cache(s)...`);
            Promise.all(
              cacheNames.map((cacheName) => caches.delete(cacheName))
            ).then(() => {
              console.log('✅ All caches deleted');
            });
          }
        });
      }
    }
  }, []);

  return (
    <div className="App">
      <ErrorBoundary name="App" message="L'application a rencontré une erreur inattendue.">
        <CircuitBreakerBanner />
        <BrowserRouter>
          <AuthProvider>
            <ModuleAccessProvider>
              <RolePermissionsProvider>
              <DemoTourProvider>
              <TeamsProvider>
                <KPISettingsProvider>
                  <GlobalSMSNotifications>
                    <AppRoutes />
                    <PWAInstallPrompt />
                    <UpdateNotification />
                    <ConnectionStatus />
                  </GlobalSMSNotifications>
                </KPISettingsProvider>
              </TeamsProvider>
              </DemoTourProvider>
              </RolePermissionsProvider>
            </ModuleAccessProvider>
          </AuthProvider>
        </BrowserRouter>
        <Toaster />
      </ErrorBoundary>
    </div>
  );
}

export default App;
